import type { AxiosError } from 'axios'
import type { History } from 'history'
import { put, takeLatest, takeLeading, call, delay } from 'redux-saga/effects'

import { RoleStatus } from '@b-stock/account-api-client'
import type {
  AccessTokenContent,
  ManagedAccessToken,
} from '@b-stock/fe-core/bstock-react'
import { ddLogger, showSnackbar } from '@b-stock/fe-core/bstock-react'

import notification from '@api/notification'
import personApi from '@api/person'
import signUpApi from '@api/signUp'
import { buildPasswordlessLoginUrl } from '@helpers/auth/passwordlessLogin'
import { getSignUpState } from '@helpers/getSignUpState'
import { gtag, gtagFields } from '@helpers/gtag'
import {
  clearPendingPersonUpdate,
  setPendingPersonUpdate,
} from '@helpers/pendingPersonUpdate'
import { sanitizeEmail } from '@helpers/sanitize'
import { replace } from '@helpers/windowLocation'
import {
  accessTokenFulfilled,
  accessTokenPending,
  accessTokenRejected,
} from '@store/auth/login/actions'

import type {
  CheckEmailActionPayload,
  SubmitEmailActionPayload,
  SetPasswordActionPayload,
  FetchVerificationCodeActionPayload,
} from './actions'
import {
  checkEmailPending,
  checkEmailFulfilled,
  checkEmailRejected,
  submitEmailPending,
  submitEmailFulfilled,
  submitEmailRejected,
  setPasswordPending,
  setPasswordFulfilled,
  setPasswordRejected,
  fetchVerificationCodePending,
  fetchVerificationCodeFulfilled,
  fetchVerificationCodeRejected,
} from './actions'
import types from './types'

const { submitEmail, fetchVerificationCode } = signUpApi
const { updatePerson, checkExistingPersons } = personApi

const MAX_TRANSIENT_ATTEMPTS = 3
const TRANSIENT_RETRY_BACKOFF_MS = 500
// Axios/Node transport error codes (no HTTP response) that warrant a retry.
const TRANSIENT_ERROR_CODES = new Set([
  'ECONNABORTED', // axios request timeout
  'ETIMEDOUT',
  'ERR_NETWORK',
  'ECONNRESET',
  'ENETUNREACH',
  'EAI_AGAIN',
])

// Transient = retryable: no HTTP response (network/timeout) or a 5xx. Anything
// with a response (4xx) or a malformed error is permanent and surfaced at once.
export const isTransientError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const axiosError = error as AxiosError
  const status = axiosError.response?.status
  if (typeof status === 'number') return status >= 500
  if (axiosError.response) return false

  // No `response` means no HTTP reply came back, so `request` here marks a
  // genuine network failure (Axios sets it on every reply, but those are caught
  // above). Transport `code`s cover the rest (e.g. ECONNABORTED).
  return (
    Boolean(axiosError.request) ||
    (typeof axiosError.code === 'string' &&
      TRANSIENT_ERROR_CODES.has(axiosError.code))
  )
}

// Retry `fn` on transient failures with a short backoff, up to
// MAX_TRANSIENT_ATTEMPTS; non-transient failures and the final attempt rethrow.
// Delegating generator (`yield*`) so the wrapped `call` still surfaces in the
// parent saga's effect stream.
export function* callWithTransientRetry<Args extends unknown[], Return>(
  fn: (...args: Args) => Return,
  ...args: Args
): Generator<unknown, Awaited<Return>, unknown> {
  for (let attempt = 1; ; attempt++) {
    try {
      return (yield call(fn, ...args)) as Awaited<Return>
    } catch (error) {
      if (attempt >= MAX_TRANSIENT_ATTEMPTS || !isTransientError(error)) {
        throw error
      }
      yield delay(TRANSIENT_RETRY_BACKOFF_MS * attempt)
    }
  }
}

// The error body the sagas below read off a rejected request.
type SignUpErrorBody = {
  errorMessage?: string
  remainingSeconds?: number
}

/**
 * A bare cast, **not** a type guard — nothing is validated. Every `catch` here
 * reaches for the same handful of fields on whatever was thrown, so the cast is
 * centralised instead of repeated (the same approach `logSignUpFailure` below
 * already takes). It is safe only because every read through the result is
 * optional-chained: a plain `Error` or a `TypeError` from a logic fault yields
 * `undefined` for `response`/`code` rather than throwing.
 *
 * `Partial<>` because it may not be an Axios error at all, and `| undefined` on
 * the body because a transport failure carries no response to read.
 */
const toPartialAxiosError = (
  error: unknown
): Partial<AxiosError<SignUpErrorBody | undefined>> =>
  error as Partial<AxiosError<SignUpErrorBody | undefined>>

// Log a fatal sign-up failure for engineering. Callers then dispatch the
// access-token + set-password rejections so the error surfaces on the
// set-password page instead of hanging on the spinner.
const logSignUpFailure = (
  error: unknown,
  message: string,
  target: { personId?: string; email?: string }
): void => {
  const axiosError = error as Partial<AxiosError> | undefined
  ddLogger.error(
    message,
    {
      error: {
        message: axiosError?.message ?? null,
        statusCode: axiosError?.response?.status ?? null,
      },
      target,
    },
    error as Error
  )
}

function* checkEmailSaga({ payload }: { payload: CheckEmailActionPayload }) {
  try {
    yield put(checkEmailPending())
    const { email } = payload
    yield put(
      checkEmailFulfilled({
        email: sanitizeEmail(email),
      })
    )
  } catch (error) {
    ddLogger.error('check email error', {}, error as Error)
    yield put(checkEmailRejected(error))
  }
}

export function* submitEmailSaga({
  payload,
}: {
  payload: SubmitEmailActionPayload
}) {
  const { email, id, formatMessage } = payload
  const sanitizedEmail = sanitizeEmail(email)
  const { siteAbb } = getSignUpState()

  try {
    yield put(submitEmailPending())

    let payload
    // Cast on the right: `yield call` is typed `any`, so a left-hand annotation
    // silences nothing. `| undefined` because the guards below are load-bearing
    // — this endpoint answers with an empty body for an unknown email.
    const existingPerson = (yield call(checkExistingPersons, {
      email: sanitizedEmail,
    })) as Awaited<ReturnType<typeof checkExistingPersons>> | undefined
    // person exists, proceed with verification step, but do not resend verification code
    if (id || existingPerson?._id) {
      payload = {
        _id: id || existingPerson?._id || '',
        email: sanitizedEmail,
        isEmailVerified: false,
        exists: true,
      }
    } else {
      // otherwise create person (email verification code sent automatically)
      const accountData = (yield call(submitEmail, {
        email: sanitizedEmail,
        ...(siteAbb ? { siteAbb } : {}),
      })) as Awaited<ReturnType<typeof submitEmail>>

      // TODO: remove logging of email when no longer needed for debugging
      if (siteAbb) {
        ddLogger.info(`submitting email data to sign up for site ${siteAbb}`, {
          siteAbb,
          email: sanitizedEmail,
          target: {
            accountId: accountData.data.accountId,
            personId: accountData.data._id,
            siteAbb,
          },
        })
      } else {
        ddLogger.info('submitted email data without a site abbreviation', {
          email: sanitizedEmail,
          target: {
            accountId: accountData.data.accountId,
            personId: accountData.data._id,
          },
        })
      }
      payload = accountData.data
    }

    yield put(submitEmailFulfilled(payload))
  } catch (error) {
    ddLogger.error('Callback catch to toast error', {}, error as Error)

    const status = toPartialAxiosError(error).response?.status

    if (status === 422) {
      if (formatMessage) {
        yield call(showSnackbar, {
          content: formatMessage({
            id: 'SignUp.submitEmail.callToAction.emailRejected',
          }),
          options: { type: 'error' },
        })
      }
    }

    yield put(submitEmailRejected(error))
  }
}

export function* fetchVerificationCodeSaga({
  payload,
}: {
  payload: FetchVerificationCodeActionPayload
}) {
  const { exists, hasCredentials, isEmailVerified, id, formatMessage, email } =
    payload

  try {
    yield put(fetchVerificationCodePending())

    let resolvedId = id
    if (!resolvedId && email) {
      const existingPerson = (yield call(checkExistingPersons, {
        email: sanitizeEmail(email),
      })) as Awaited<ReturnType<typeof checkExistingPersons>> | undefined
      resolvedId = existingPerson?._id ?? ''
    }

    if (!resolvedId) {
      if (formatMessage) {
        yield call(showSnackbar, {
          content: formatMessage({ id: 'SignUp.verifyEmail.resendErrorId' }),
          options: { type: 'error', autoClose: true },
        })
      }
      throw new Error('cannot fetch verification code without id')
    }

    // Is email is existing or already verified
    if (exists === false || hasCredentials || isEmailVerified) {
      if (formatMessage) {
        yield call(showSnackbar, {
          content: formatMessage({ id: 'SignUp.verifyEmail.resendEmailExist' }),
          options: { type: 'error', autoClose: true },
        })
      }
      return
    }

    yield call(fetchVerificationCode, { id: resolvedId })
    yield put(fetchVerificationCodeFulfilled())
    if (email && formatMessage) {
      yield call(showSnackbar, {
        content: formatMessage({ id: 'SignUp.verifyEmail.resend' }, { email }),
        options: { autoClose: true },
      })
    }
  } catch (error) {
    ddLogger.error('Fetch verification code error', {}, error as Error)

    const axiosError = toPartialAxiosError(error)
    const status = axiosError.response?.status
    // `||` not `??`, so an empty `errorMessage` still falls back to `message`.
    const errorMessage =
      axiosError.response?.data?.errorMessage || axiosError.message
    const remainingSeconds = axiosError.response?.data?.remainingSeconds
    const errorPayload = {
      status,
      message: errorMessage,
      remainingSeconds,
    }

    // 409: the person has no code to resend (e.g. an invitee, whose invite
    // link replaces the code). The verify step shows this inline on the code
    // field from the rejected state, so skip the generic, auto-closing toast.
    if (formatMessage && status !== 409) {
      yield call(showSnackbar, {
        content: formatMessage(
          { id: 'SignUp.verifyEmail.resendError' },
          { error: errorMessage }
        ),
        options: { type: 'error', autoClose: true },
      })
    }
    yield put(fetchVerificationCodeRejected(errorPayload))
  }
}

/**
 * Finish sign-up through a real FusionAuth login (FP-3569) instead of the
 * registration session, so FusionAuth's MFA login policy applies.
 *
 * The registration session was only needed for the setup calls above; it is
 * dropped here so the only way in is the FusionAuth login. The `/login`
 * callback then lands the not-yet-onboarded user on `/create-profile` (or the
 * MAO `/buy/` target), the same destinations as the token path.
 */
function* handOffToPasswordlessLogin({
  passwordlessCode,
  subflow,
  redirectAfterSignup,
  managedAccessToken,
  personId,
  history,
}: {
  passwordlessCode: string
  subflow?: string
  redirectAfterSignup?: string
  managedAccessToken: ManagedAccessToken
  personId?: string
  history: History
}): Generator {
  try {
    yield call([managedAccessToken, 'clearTokenCookies'])
  } catch (error) {
    // Nothing depends on it: FusionAuth's login replaces the session anyway.
    ddLogger.warn('Could not clear the registration session before hand-off', {
      error,
      target: { personId },
    })
  }

  ddLogger.info('sign-up hand-off to passwordless login', {
    target: { personId },
  })
  try {
    const url = (yield call(
      buildPasswordlessLoginUrl,
      passwordlessCode,
      undefined,
      subflow === 'mao' ? { subflow, redirectAfterSignup } : {}
    )) as string
    replace(url)
  } catch (error) {
    ddLogger.warn('Could not complete the sign-up login hand-off', {
      error,
      target: { personId },
    })
    // Account setup succeeded; the new password can be used for ordinary login.
    history.push('/')
  }
}

export function* setUserData({
  jwtData,
  payload,
  history,
  managedAccessToken,
  passwordlessCode,
}: {
  jwtData: AccessTokenContent
  payload: SetPasswordActionPayload
  history: History
  managedAccessToken: ManagedAccessToken
  /** When set, sign-up ends with a FusionAuth passwordless login (FP-3569). */
  passwordlessCode?: string
}) {
  const {
    country,
    brid,
    phone,
    // Defaulted alongside `subscribe` for the same reason: this block only ever
    // sends an opt-out, and `active: undefined` would be dropped from the PATCH
    // body entirely rather than recorded as `false`.
    acceptMarketingSolicitation = false,
    subscribe = false,
    redirectAfterSignup,
    subflow,
    email,
    id,
  } = payload

  // Prefer the verified id the set-password flow holds; the token's personId can
  // be temporarily empty during sign-up, which produced the null-name bug.
  const personId = id || jwtData.personId

  try {
    yield put(accessTokenPending())

    yield call([managedAccessToken, 'createRole'], {
      accountId: jwtData.accountId,
      role: 'buyer',
      params: { status: RoleStatus.Intended, reason: 'SELF_ONBOARDING' },
    })
  } catch (error) {
    logSignUpFailure(error, 'Failed to create buyer role during sign-up', {
      personId,
      email,
    })
    yield put(accessTokenRejected(error))
    yield put(setPasswordRejected(error))
    return
  }

  if (!brid) {
    ddLogger.warn(
      'Updating business verification data in setUserData, but no brid value available',
      { target: { accountId: jwtData.accountId, personId } }
    )
  }

  if (!country) {
    ddLogger.warn(
      'Updating business verification data in setUserData, but no country value available',
      { target: { accountId: jwtData.accountId, personId } }
    )
  }

  // firstName/lastName are already persisted atomically as part of setPassword
  // (see setPasswordSaga) by the time this runs. Phone isn't part of that
  // backend call, so it still needs its own durable, retried write here.
  if (phone?.trim()) {
    try {
      // Guard against a malformed write to `/v1/people/:id` with no id.
      if (!personId) {
        throw new Error(
          'Cannot persist registration phone: no usable person id available'
        )
      }

      // Persist the intent before writing and clear it only after a confirmed
      // success, so a failed write survives reload and is replayed on the next
      // authenticated visit.
      yield call(setPendingPersonUpdate, { personId, phone })

      yield* callWithTransientRetry(updatePerson, {
        id: personId,
        data: { phone },
      })

      yield call(clearPendingPersonUpdate)
    } catch (error) {
      logSignUpFailure(
        error,
        'Failed to persist registration phone during sign-up',
        { personId, email }
      )
      yield put(accessTokenRejected(error))
      yield put(setPasswordRejected(error))
      return
    }
  }

  // Notifications are non-critical: isolate each so a marketing/SMS failure
  // can't abort an otherwise-complete sign-up.
  if (!acceptMarketingSolicitation) {
    // Only fired to opt the user out; we never send an opt-in from here.
    try {
      yield call(
        notification.updateMarketingPreference,
        acceptMarketingSolicitation,
        { personId }
      )
    } catch (error) {
      ddLogger.warn('Marketing preference update failed during sign-up', {
        error,
        target: { personId, email },
      })
    }
  }

  try {
    yield call(
      notification.setTransactionalSmsFromOnboarding,
      personId,
      subscribe
    )
  } catch (error) {
    ddLogger.warn('Transactional SMS opt-in failed during sign-up', {
      error,
      target: { personId, email },
    })
  }

  yield put(accessTokenFulfilled())
  gtag({
    event: gtagFields.event.sign_up,
    method: gtagFields.method.fusionAuth,
    platform: gtagFields.platform,
  })

  // If MAO subflow, skip profile creation and redirect to target /buy/ URL
  if (subflow === 'mao') {
    // A MAO link always carries `redirectAfterSignup`; without one there is
    // nowhere to send the user, which is the same dead end as a non-/buy/ URL.
    if (!redirectAfterSignup?.includes('/buy/')) {
      history.push('/error')
      return
    }
    if (passwordlessCode) {
      yield* handOffToPasswordlessLogin({
        passwordlessCode,
        subflow,
        redirectAfterSignup,
        managedAccessToken,
        personId,
        history,
      })
      return
    }
    replace(redirectAfterSignup)
    return
  }
  if (passwordlessCode) {
    yield* handOffToPasswordlessLogin({
      passwordlessCode,
      managedAccessToken,
      personId,
      history,
    })
    return
  }
  history.push('/create-profile')
}

export function* setPasswordSaga({
  payload,
  history,
  managedAccessToken,
}: {
  payload: SetPasswordActionPayload
  history: History
  managedAccessToken: ManagedAccessToken
}) {
  try {
    yield put(setPasswordPending())

    // `setPassword` wants plain strings. Each of these is guaranteed by the
    // wizard (the person id is resolved before dispatch, password and code are
    // both schema-required), and an empty one fails the request exactly as a
    // missing one does today.
    const {
      id = '',
      password = '',
      siteAbb,
      verificationCode = '',
      firstName,
      lastName,
    } = payload
    const trimmedFirstName = firstName?.trim() || undefined
    const trimmedLastName = lastName?.trim() || undefined

    if (!trimmedFirstName && !trimmedLastName) {
      // With no name to write, an empty-body PATCH would 200 and the loss would
      // be invisible. Log loudly and let the create-profile repair flow collect
      // the name rather than blocking an otherwise-valid sign-up.
      ddLogger.error(
        'Registration name missing from sign-up state at set-password',
        { target: { personId: id } }
      )
    }

    const setPasswordResult = (yield call([managedAccessToken, 'setPassword'], {
      peopleId: id,
      params: {
        newPassword: password,
        verificationCode,
        ...(trimmedFirstName ? { firstName: trimmedFirstName } : {}),
        ...(trimmedLastName ? { lastName: trimmedLastName } : {}),
      },
    })) as Awaited<ReturnType<ManagedAccessToken['setPassword']>>
    // Sign-up's setup calls below still need the registration session, so the
    // tokens are adopted either way; the code only changes how sign-up ends.
    const { decodedAccessToken, passwordlessCode } = setPasswordResult

    // `setPassword` resolves with a null token when it couldn't mint one.
    // `setUserData` immediately reads `accountId`/`personId` off it, so without
    // this the flow died on a TypeError deeper in; throwing here reaches the
    // same `catch` below and surfaces the same rejection, just deliberately.
    if (!decodedAccessToken) {
      throw new Error(
        'Password was set but no access token was returned; cannot continue sign-up'
      )
    }

    ddLogger.info('User set password during registration', {
      target: { personId: id, siteAbb },
    })

    yield put(setPasswordFulfilled())
    gtag({
      event: gtagFields.event.login,
      website_flow: gtagFields.website_flow,
      method: gtagFields.method.fusionAuth,
      ...(siteAbb ? { marketplace: siteAbb } : {}),
    })
    gtag({
      event: gtagFields.event.sign_up_submit,
      method: gtagFields.method.fusionAuth,
      platform: gtagFields.platform,
    })

    yield call(setUserData, {
      jwtData: decodedAccessToken,
      payload,
      history,
      managedAccessToken,
      ...(passwordlessCode ? { passwordlessCode } : {}),
    })
  } catch (error) {
    const axiosError = toPartialAxiosError(error)
    ddLogger.error('Failed to set password during registration', {
      error: {
        message: axiosError.message ?? null,
        statusCode: axiosError.response?.status ?? null,
      },
      target: {
        personId: payload.id,
        email: payload.email,
      },
    })
    yield put(setPasswordRejected(error))
  }
}

// `takeLatest`/`takeLeading` hand the worker a full action, but each worker
// above declares only the fields it destructures. These wrappers bridge the two
// without an `as any` at the watcher — the same shape `login/saga` uses — and
// keep the workers directly callable with a plain object from the tests.
type CheckEmailSagaAction = Parameters<typeof checkEmailSaga>[0] & {
  type: string
}
function* onCheckEmail(action: CheckEmailSagaAction) {
  yield* checkEmailSaga(action)
}

type SubmitEmailSagaAction = Parameters<typeof submitEmailSaga>[0] & {
  type: string
}
function* onSubmitEmail(action: SubmitEmailSagaAction) {
  yield* submitEmailSaga(action)
}

type FetchVerificationCodeSagaAction = Parameters<
  typeof fetchVerificationCodeSaga
>[0] & { type: string }
function* onFetchVerificationCode(action: FetchVerificationCodeSagaAction) {
  yield* fetchVerificationCodeSaga(action)
}

type SetPasswordSagaAction = Parameters<typeof setPasswordSaga>[0] & {
  type: string
}
function* onSetPassword(action: SetPasswordSagaAction) {
  yield* setPasswordSaga(action)
}

export default function* signupSaga() {
  // redux-saga propagates a throwing worker up through `takeLatest` to this
  // parent task, which would otherwise take the whole root saga down with it.
  // Patterned after `signinSaga` in ../signIn/saga, but read the cost first:
  //
  // Last resort only — every worker above has its own try/catch, so reaching
  // here means a fault outside them (redux-saga plumbing, or one of the `on*`
  // wrappers). When it does fire, redux-saga has already cancelled all four
  // watchers, and this saga then returns, so check-email, submit-email,
  // resend-code and set-password all stop responding for the rest of the page
  // session. `signinSaga`'s equivalent catch costs one watcher; this one costs
  // four. Only `ddLogger.error` fires: no snackbar, no error state, nothing the
  // user can see — they get a dead form until they reload.
  //
  // A user-facing fallback is deliberately not attempted here. There is no
  // top-level error action in this store to dispatch (`store/ui` carries only
  // SET_STEP / INIT_LOAD_FULFILLED), and a `showSnackbar` call would need a new
  // locale key, so it belongs in its own change rather than a lint pass.
  //
  // Re-throwing is worse, not better: neither ../saga nor ../../rootSaga
  // restarts anything (both just `console.error`), and both compose with
  // `all()`, so re-throwing would cancel the login and sign-in watchers too.
  try {
    yield takeLatest(types.CHECK_EMAIL, onCheckEmail)
    // `takeLeading` here so that if the user manages to mash click/enter/whatever
    // and fires off two submit email actions in quick succession, the second (and
    // any subsequent) one is discarded
    yield takeLeading(types.SUBMIT_EMAIL, onSubmitEmail)
    yield takeLatest(types.FETCH_VERIFICATION_CODE, onFetchVerificationCode)
    yield takeLatest(types.SET_PASSWORD, onSetPassword)
  } catch (error) {
    ddLogger.error(
      'SignUp error',
      {},
      error instanceof Error
        ? error
        : new Error('Original error did not have a stack trace')
    )
  }
}
